import { NAV_LOGO_LIGHT, NAV_LOGO_DARK } from "@/utils/constants";
import { useState } from "react";
import { Menu, X, ShoppingBag, Heart, Flower, Smartphone, ChevronDown, Sparkles, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShopifyCartSidebar } from "./ShopifyCartSidebar";
import UserMenu from "./UserMenu";
import SearchBar from "./SearchBar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/components/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useFloatingActions } from "@/contexts/FloatingActionsContext";
import { usePWA } from "@/hooks/usePWA";
import BohoTextLogo from "./BohoTextLogo";
import { MegaMenu } from "./MegaMenu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const BohoNav = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
  const [menuCloseTimeout, setMenuCloseTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const { favorites } = useFavorites();
  const { isInstallable, isInstalled, promptInstall } = usePWA();

  const navItems = [
    { name: "Home", href: "/" },
    { name: "All Products", href: "/products" },
    { name: "Collections", href: "/collections", hasDropdown: true },
    { name: "Style\u00A0Guides", href: "/blog" },
    { name: "Slow\u00A0Living", href: "/slow-living" },
  ];

  const isActive = (href: string) => {
    if (href === "/" && location.pathname === "/") return true;
    if (href !== "/" && location.pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <nav className="sticky top-0 z-50 w-full bg-background/60 backdrop-blur-3xl border-b border-border/10 shadow-lg shadow-primary/5" role="navigation" aria-label="Main navigation" style={{ margin: '0 !important', width: '100vw !important', maxWidth: 'none !important' }}>
      <div className="w-full" style={{ margin: '0 !important', maxWidth: 'none !important' }}>
        <div className="grid grid-cols-[auto_1fr_auto] gap-4 h-20 items-center relative">
          {/* Logo */}
          <Link to="https://bohostyleco.com" className="flex items-center shrink-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md transition-all duration-300 hover:scale-105">
            <BohoTextLogo size="md" />
          </Link>

          {/* Center Column: Desktop Navigation + Search */}
          <div className="hidden md:flex items-center justify-center gap-4">
            {/* Desktop Navigation */}
            <div className="flex items-center gap-standard bg-background/5 backdrop-blur-sm rounded-full px-6 py-2 border border-border/10" role="menubar">
              {navItems.map((item) => {
                if (item.hasDropdown) {
                  const handleMenuClose = () => {
                    const timeout = setTimeout(() => {
                      setIsMegaMenuOpen(false);
                    }, 200);
                    setMenuCloseTimeout(timeout);
                  };

                  const handleMenuOpen = () => {
                    if (menuCloseTimeout) {
                      clearTimeout(menuCloseTimeout);
                      setMenuCloseTimeout(null);
                    }
                    setIsMegaMenuOpen(true);
                  };

                  return (
                    <div
                      key={item.name}
                      className="relative"
                      onMouseEnter={handleMenuOpen}
                    >
                      <button
                        className={`relative body-small-medium transition-all duration-300 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-3 py-2 flex items-center gap-1 ${
                          isActive(item.href) 
                            ? 'text-primary' 
                            : 'text-muted-foreground hover:text-primary'
                        } after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-primary after:transition-all after:duration-300 hover:after:w-full ${
                          isActive(item.href) ? 'after:w-full' : ''
                        }`}
                      >
                        {item.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${isMegaMenuOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* MegaMenu positioned within the same hover container */}
                      <MegaMenu 
                        isOpen={isMegaMenuOpen} 
                        onClose={() => setIsMegaMenuOpen(false)}
                        onMouseEnter={handleMenuOpen}
                        onMouseLeave={handleMenuClose}
                      />
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.name === "Home" ? "/" : item.href}
                    role="menuitem"
                    replace={item.href === '/products'}
                    onClick={(e) => {
                      if (item.href === '/products') {
                        e.preventDefault();
                        navigate('/products', { replace: true });
                      }
                    }}
                    className={`relative body-small-medium transition-all duration-300 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-3 py-2 ${
                      isActive(item.name === "Home" ? "/" : item.href) 
                        ? 'text-primary' 
                        : 'text-muted-foreground hover:text-primary'
                    } after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-primary after:transition-all after:duration-300 hover:after:w-full ${
                      isActive(item.name === "Home" ? "/" : item.href) ? 'after:w-full' : ''
                    }`}
                    aria-current={isActive(item.name === "Home" ? "/" : item.href) ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-standard bg-background/5 backdrop-blur-sm rounded-full px-4 py-2 border border-border/10">
            
            <Link to={user ? "/favorites" : "/auth"} aria-label={user ? "View favorites" : "Sign in to view favorites"}>
              <Button 
                variant="ghost" 
                size="sm" 
                aria-label="Favorites" 
                className="hover:bg-transparent hover:text-favorite"
              >
                <Heart 
                  className={`w-5 h-5 ${favorites.length > 0 ? 'text-favorite' : ''}`} 
                  fill={favorites.length > 0 ? 'currentColor' : 'none'}
                  aria-hidden="true" 
                />
                <span className="sr-only">Favorites</span>
              </Button>
            </Link>
            
            {/* PWA Install Button */}
            {isInstallable && !isInstalled && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={promptInstall}
                aria-label="Install app"
                className="hover:bg-transparent hover:text-primary"
              >
                <Smartphone className="w-5 h-5" aria-hidden="true" />
                <span className="sr-only">Install app</span>
              </Button>
            )}
            <ShopifyCartSidebar showBadgeOnScroll={false}>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  aria-label="Shopping cart"
                >
                <ShoppingBag className="w-5 h-5" aria-hidden="true" />
                <span className="sr-only">Shopping cart</span>
              </Button>
            </ShopifyCartSidebar>
            <UserMenu />
          </div>

          {/* Mobile Menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                className="md:hidden" 
                size="sm" 
                aria-label="Open mobile menu" 
                aria-expanded={isMobileMenuOpen}
              >
                <Menu className="w-5 h-5" aria-hidden="true" />
                <span className="sr-only">Open mobile menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 sm:w-96 bg-background border-border">
              <div className="flex flex-col space-standard mt-6">
                {/* Mobile Navigation Links */}
              <div className="space-standard">
                {navItems.map((item) => {
                    if (item.name === "Home") {
                      return (
                        <Link
                          key={item.name}
                          to="/"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-3 py-2 text-lg font-medium leading-none text-foreground transition-standard hover:text-primary hover:bg-muted/50 rounded-md`}
                        >
                          {item.name}
                        </Link>
                      );
                    }
                    
                    if (item.hasDropdown) {
                      return (
                        <Collapsible
                          key={item.name}
                          open={isCollectionsOpen}
                          onOpenChange={setIsCollectionsOpen}
                          className="space-y-2"
                        >
                          <CollapsibleTrigger asChild>
                            <Link
                              to="/collections"
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center justify-between w-full px-3 py-2 text-lg font-medium leading-none text-foreground transition-standard hover:text-primary hover:bg-muted/50 rounded-md"
                            >
                              {item.name}
                              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isCollectionsOpen ? 'rotate-180' : ''}`} />
                            </Link>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="pl-4 space-y-1">
                            {/* Apparel */}
                            <div className="space-y-1 pb-2">
                              <Link to="/collections/apparel" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-muted-foreground hover:text-primary px-3 py-1 block">Apparel</Link>
                              <Link to="/collections/goddess" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">
                                <Sparkles className="h-4 w-4 text-primary" />
                                Goddess Collection
                              </Link>
                              <Link to="/collections/tops" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Tops</Link>
                              <Link to="/collections/bottoms" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Bottoms</Link>
                              <Link to="/collections/dresses" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Dresses</Link>
                              <Link to="/collections/one-pieces" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">One-Pieces</Link>
                              <Link to="/collections/lounge-wear" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Lounge Wear</Link>
                            </div>
                            {/* Jewelry */}
                            <div className="space-y-1 pb-2">
                              <Link to="/collections/jewelry" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-muted-foreground hover:text-primary px-3 py-1 block">Jewelry</Link>
                              <Link to="/collections/jewelry?q=necklaces" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Necklaces</Link>
                              <Link to="/collections/jewelry?q=bracelets" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Bracelets</Link>
                              <Link to="/collections/jewelry?q=rings" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Rings</Link>
                              <Link to="/collections/jewelry?q=earrings" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Earrings</Link>
                            </div>
                            {/* Accessories */}
                            <div className="space-y-1 pb-2">
                              <Link to="/collections/accessories" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-muted-foreground hover:text-primary px-3 py-1 block">Accessories</Link>
                              <Link to="/collections/bags" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Bags</Link>
                              <Link to="/collections/shoes" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Shoes</Link>
                              <Link to="/collections/accessories?q=hats" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Hats & Scarves</Link>
                              <Link to="/collections/accessories?q=belts" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Belts</Link>
                            </div>
                            {/* Lifestyle & Special */}
                            <div className="space-y-1 pb-2">
                              <div className="text-sm font-semibold text-muted-foreground px-3 py-1">Lifestyle & More</div>
                              <Link to="/collections/lifestyle" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Lifestyle & Home Decor</Link>
                              <Link to="/collections/bath-body" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Bath & Body</Link>
                              <Link to="/collections/chrome-hearts" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">Chrome Hearts 🖤</Link>
                              <Link to="/collections/sale" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-1.5 text-base text-foreground hover:text-primary hover:bg-muted/50 rounded-md">
                                <Flame className="h-4 w-4 text-orange-500" />
                                Sale
                              </Link>
                            </div>
                            {/* View All */}
                            <Link to="/collections" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-primary hover:bg-muted/50 rounded-md border-t mt-2 pt-2">View All Collections →</Link>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    }
                    
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        replace={item.href === '/products'}
                        onClick={(e) => {
                          setIsMobileMenuOpen(false);
                          if (item.href === '/products') {
                            e.preventDefault();
                            navigate('/products', { replace: true });
                          }
                        }}
                        className={`block px-3 py-2 text-lg font-medium leading-none text-foreground transition-standard hover:text-primary hover:bg-muted/50 rounded-md`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                  
                  {/* Admin Link - Mobile Only */}
                  {user && isAdmin() && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-3 py-2 text-lg font-medium leading-none text-foreground transition-standard hover:text-primary hover:bg-muted/50 rounded-md border-t pt-4 mt-4`}
                    >
                      Admin Dashboard
                    </Link>
                  )}
                </div>

                {/* Mobile Actions */}
                <div className="space-standard">
                  <div className="control-row">
                    <span className="body-small text-muted-color">Quick Actions</span>
                  </div>
                  <div className="space-compact">
                    <div className="flex items-center justify-between">
                      <Link to={user ? "/favorites" : "/auth"} onClick={() => setIsMobileMenuOpen(false)} className="flex-1 mr-2">
                        <Button 
                          variant="outline" 
                          className="w-full hover:text-favorite"
                        >
                          <Heart 
                            className={`w-4 h-4 mr-2 ${favorites.length > 0 ? 'text-favorite' : ''}`}
                            fill={favorites.length > 0 ? 'currentColor' : 'none'}
                          />
                          Favorites
                        </Button>
                      </Link>
                      
                    </div>
                    
                    {/* PWA Install Button - Mobile */}
                    {isInstallable && !isInstalled && (
                      <Button 
                        variant="outline" 
                        className="w-full hover:text-primary"
                        onClick={() => {
                          promptInstall();
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <Smartphone className="w-4 h-4 mr-2" />
                        Install App
                      </Button>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <ShopifyCartSidebar showBadgeOnScroll={false}>
                        <Button variant="outline" className="flex-1 mr-2">
                          <ShoppingBag className="w-4 h-4 mr-2" />
                          Cart
                        </Button>
                      </ShopifyCartSidebar>
                      <UserMenu />
                    </div>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default BohoNav;